import React, { useState, useMemo, useEffect } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconTrash,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconUpload,
  IconDownload,
  IconFileText,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchHrDepartments,
  createHrDepartment,
  updateHrDepartment,
  deleteHrDepartment,
  fetchHrCompany,
  fetchHrBranches,
  fetchHrEmployees,
  HrDepartment,
  HrBranch,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, placeholder, type = "text", className = "", error, required, ...rest }, ref) => {
    return (
      <div className={`w-full space-y-1 ${className}`}>
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            {...rest}
            className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
          />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    );
  }
);
InputField.displayName = "InputField";

const SelectInput = ({
  label,
  value,
  setValue,
  selectOptions = [],
  className = "",
  required,
  disabled,
}: {
  label: string;
  value?: string | number;
  setValue?: (val: string) => void;
  selectOptions?: { value: string | number; text: string }[] | false;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className={`space-y-1 w-full ${className}`}>
      <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative flex items-center w-full">
        <select
          value={value}
          onChange={(e) => setValue && setValue(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="">Select</option>
          {selectOptions &&
            selectOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.text}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
};

interface OrgNode {
  name: string;
  children?: OrgNode[];
}

function TreeNode({ node, isRoot = false }: { node: OrgNode; isRoot?: boolean }) {
  const hasChildren = node.children && node.children.length > 0;
  
  const cardStyle = isRoot
    ? "bg-[#212121] text-white border border-[#212121] px-6 py-3 rounded-xl font-extrabold uppercase tracking-wider shadow-md"
    : "bg-white text-[#212121] border border-gray-200 border-l-4 border-l-[#FBD037] px-5 py-2.5 rounded-xl font-semibold shadow-xs hover:border-gray-300 hover:shadow-sm";

  return (
    <li>
      <div className={`flex items-center justify-center text-xs whitespace-nowrap transition-all duration-300 hover:scale-105 cursor-pointer ${cardStyle}`}>
        <span>{node.name}</span>
      </div>
      {hasChildren && (
        <ul>
          {node.children!.map((child, idx) => (
            <TreeNode key={idx} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Department() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"table" | "hierarchy">("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [entriesLimit, setEntriesLimit] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);

  // Simple form states
  const [deptName, setDeptName] = useState("");
  const [parentDeptId, setParentDeptId] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [editingDeptId, setEditingDeptId] = useState<string | null>(null);
  const [selectedDept, setSelectedDept] = useState<HrDepartment | null>(null);


  // Fetch company info
  const [companyId, setCompanyId] = useState("");
  const [branches, setBranches] = useState<HrBranch[]>([]);
  const [addBranchId, setAddBranchId] = useState("");
  const [employees, setEmployees] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    fetchHrCompany().then(res => {
      if (res.success && res.data) {
        setCompanyId(res.data.id || res.data._id || "");
      }
    }).catch(console.error);
    fetchHrBranches(1, 100).then(res => {
      if (res.success && res.data?.branches) {
        setBranches(res.data.branches);
        if (res.data.branches.length > 0) {
          setAddBranchId(res.data.branches[0].id);
        }
      }
    }).catch(console.error);
    fetchHrEmployees(1, 200).then(res => {
      if (res.success && res.data?.employees) {
        setEmployees(
          res.data.employees.map((e: any) => ({
            id: e.id,
            name: `${e.firstName || ""}${e.lastName ? " " + e.lastName : ""}`.trim() || e.employeeId || e.id,
          }))
        );
      }
    }).catch(console.error);
  }, []);

  // Debounce search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch departments
  const { data, isLoading } = useQuery({
    queryKey: ["departments", debouncedSearch],
    queryFn: () => fetchHrDepartments(1, 50, debouncedSearch),
  });

  const departments = data?.data?.departments || [];

  // Filter departments for the Add modal by the currently selected branch
  const branchDepartments = departments.filter(
    (d: HrDepartment) => !addBranchId || d.branchId === addBranchId
  );

  // Mutations
  const createMutation = useMutation({
    mutationFn: createHrDepartment,
    onSuccess: () => {
      toast.success("Department created successfully!");
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      setSidebarMode(null);
      setDeptName("");
      setParentDeptId("");
      setStatus("ACTIVE");
      setEditingDeptId(null);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to create department"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => updateHrDepartment(id, payload),
    onSuccess: () => {
      toast.success("Department updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      setSidebarMode(null);
      setDeptName("");
      setParentDeptId("");
      setStatus("ACTIVE");
      setEditingDeptId(null);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update department"),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHrDepartment,
    onSuccess: () => {
      toast.success("Department deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      setSidebarMode(null);
      setDeptName("");
      setParentDeptId("");
      setStatus("ACTIVE");
      setEditingDeptId(null);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to delete department"),
  });

  // Handlers
  const handleEditClick = (dept: HrDepartment) => {
    setSelectedDept(dept);
    setEditingDeptId(dept.id);
    setDeptName(dept.departmentName);
    setParentDeptId(dept.parentDepartmentId || "");
    setStatus(dept.status || "ACTIVE");
    setSidebarMode("EDIT");
  };

  const handleDeleteClick = (dept: HrDepartment) => {
    setSelectedDept(dept);
    setSidebarMode("DELETE");
  };

  const handleDeptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!deptName.trim()) {
      toast.error("Department name is required");
      return;
    }
    const payload: any = {
      departmentName: deptName.trim(),
      parentDepartmentId: parentDeptId ? parentDeptId : null,
      status: status
    };
    if (editingDeptId) {
      updateMutation.mutate({
        id: editingDeptId,
        payload
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeptDelete = (id: string) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    deleteMutation.mutate(id);
  };

  const columns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: () => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
    },
    { label:"DEPT ID", render:(s:HrDepartment)=><span className="font-semibold text-[#212121]">{s.deptId}</span> },
    { label:"DEPARTMENT NAME", render:(s:HrDepartment)=><span className="font-semibold text-[#212121] text-sm">{s.departmentName}</span> },
    { label:"PARENT DEPARTMENT", render:(s:HrDepartment)=>{
        const parent = departments.find((d: HrDepartment) => d.id === s.parentDepartmentId);
        return <span className="text-sm text-gray-700 font-medium">{parent ? parent.departmentName : "—"}</span>;
      } 
    },
    { label:"HOD", render:(s:HrDepartment)=>{
        const hod = employees.find(e => e.id === s.hodId);
        return <span className="text-sm text-gray-700">{hod ? hod.name : (s.hodId ? s.hodId.substring(0, 8) + "…" : "—")}</span>;
      }
    },
    { label:"STATUS", render:(s:HrDepartment)=><span className={`text-xs px-2 py-1 rounded-full font-semibold ${s.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.status}</span> },
    { label:"ACTIONS", render:(s:HrDepartment)=>(
      <div className="flex justify-center items-center gap-2">
        <button
          onClick={() => handleEditClick(s)}
          className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors cursor-pointer"
          title="Edit"
        >
          <IconEdit size={13} stroke={2.5} />
        </button>
        <button
          onClick={() => handleDeleteClick(s)}
          className="p-1.5 border border-red-200/55 rounded-full bg-[#FCF8F8] hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors cursor-pointer"
          title="Delete"
        >
          <IconTrash size={13} stroke={2.5} />
        </button>
      </div>
    )},
  ];

  const buildTree = (): OrgNode => {
    const rootDeps = departments.filter((d: HrDepartment) => !d.parentDepartmentId);
    const getChildren = (parentId: string): OrgNode[] => {
      return departments
        .filter((d: HrDepartment) => d.parentDepartmentId === parentId)
        .map((d: HrDepartment) => ({
          name: d.departmentName,
          children: getChildren(d.id),
        }));
    };
    return {
      name: "Company Root",
      children: rootDeps.map((d: HrDepartment) => ({
        name: d.departmentName,
        children: getChildren(d.id),
      }))
    };
  };

  const departmentTree = useMemo(() => buildTree(), [departments]);

  const activeColumns = sidebarMode
    ? columns.filter((_, idx) => [0, 1, 2, 5].includes(idx))
    : columns;

  const displayedDepartments = departments.slice(0, entriesLimit);

  // Render Sidebar Card for Department Operations
  const renderSidebar = () => {
    if (!sidebarMode) return null;

    if (sidebarMode === "DELETE" && selectedDept) {
      return (
        <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 text-center space-y-4 sticky top-[95px]">
          <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto">
            <IconTrash size={28} />
          </div>
          <h3 className="text-base font-bold text-[#212121]">Delete Department</h3>
          <p className="text-xs text-gray-500">
            Do you really want to delete department <strong>{selectedDept.departmentName}</strong>? This action cannot be undone.
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => setSidebarMode(null)}
              className="px-4 py-2.5 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition flex-1 cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteMutation.mutate(selectedDept.id)}
              disabled={deleteMutation.isPending}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition cursor-pointer border-none flex-1 shadow-sm"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      );
    }

    if (sidebarMode === "ADD" || sidebarMode === "EDIT") {
      return (
        <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden space-y-4 p-5 sticky top-[95px]">
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-[#212121]">
              {sidebarMode === "ADD" ? "Create Department" : "Edit Department"}
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                form="deptForm"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer border-none shadow-sm"
              >
                {createMutation.isPending || updateMutation.isPending ? "Saving..." : editingDeptId ? "Save" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setEditingDeptId(null);
                  setDeptName("");
                  setParentDeptId("");
                  setStatus("ACTIVE");
                }}
                className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer bg-white"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>

          {/* Input Form */}
          <form id="deptForm" onSubmit={handleDeptSubmit} className="space-y-4">
            <SectionBlock title="Department Details" icon={IconFileText}>
              <div className="flex flex-col gap-3">
                <InputField
                  label="Department Name"
                  required
                  placeholder="Enter Department name"
                  value={deptName}
                  onChange={(e) => setDeptName(e.target.value)}
                />
                <SelectInput
                  label="Parent Department"
                  value={parentDeptId}
                  setValue={setParentDeptId}
                  selectOptions={departments
                    .filter((d: HrDepartment) => d.id !== editingDeptId)
                    .map((d: HrDepartment) => ({
                      value: d.id,
                      text: d.departmentName
                    }))}
                />
                {editingDeptId && (
                  <SelectInput
                    label="Status"
                    value={status}
                    setValue={setStatus}
                    selectOptions={[
                      { value: "ACTIVE", text: "ACTIVE" },
                      { value: "INACTIVE", text: "INACTIVE" }
                    ]}
                  />
                )}
              </div>
            </SectionBlock>
          </form>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300">
      {/* Connector line styles for Org Chart */}
      <style dangerouslySetInnerHTML={{ __html: `
        .org-tree {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 40px 20px;
          overflow-x: auto;
          min-width: max-content;
        }
        .org-tree ul {
          padding-top: 24px;
          position: relative;
          transition: all 0.5s;
          display: flex;
          justify-content: center;
        }
        .org-tree li {
          text-align: center;
          list-style-type: none;
          position: relative;
          padding: 24px 8px 0 8px;
          transition: all 0.5s;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .org-tree li::before, .org-tree li::after {
          content: '';
          position: absolute;
          top: 0;
          right: 50%;
          border-top: 2px solid #CBD5E1;
          width: 50%;
          height: 24px;
        }
        .org-tree li::after {
          right: auto;
          left: 50%;
          border-left: 2px solid #CBD5E1;
        }
        .org-tree li:only-child::before {
          display: none;
        }
        .org-tree li:only-child::after {
          border-top: 0;
          border-left: 2px solid #CBD5E1;
          left: 50%;
          transform: translateX(-50%);
          height: 24px;
        }
        .org-tree li:only-child {
          padding-top: 24px;
        }
        .org-tree li:first-child::before, .org-tree li:last-child::after {
          border: 0 none;
        }
        .org-tree li:last-child::before {
          border-right: 2px solid #CBD5E1;
          border-radius: 0 8px 0 0;
        }
        .org-tree li:first-child::after {
          border-radius: 8px 0 0 0;
        }
        .org-tree ul ul::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          border-left: 2px solid #CBD5E1;
          width: 0;
          height: 24px;
          transform: translateX(-50%);
        }
        .org-tree > ul > li {
          padding-top: 0;
        }
        .org-tree > ul > li::before, .org-tree > ul > li::after {
          display: none !important;
        }
      `}} />

      {/* Header and Switcher */}
      <div className="flex items-center justify-end mb-5 px-2">
        <div className="flex items-center gap-4">
          <div className="flex bg-[#E4E7EB] rounded-xl p-1 font-sans text-xs font-bold shadow-inner">
            <button
              onClick={() => setActiveTab("table")}
              className={`px-4 py-1.5 rounded-lg transition-all ${
                activeTab === "table" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Table View
            </button>
            <button
              onClick={() => setActiveTab("hierarchy")}
              className={`px-4 py-1.5 rounded-lg transition-all ${
                activeTab === "hierarchy" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Hierarchy
            </button>
          </div>
        </div>
      </div>

      {activeTab === "table" ? (
        <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-r-3xl rounded-bl-3xl w-full">
          <div className={`${sidebarMode ? "col-span-12 xl:col-span-7" : "col-span-12"} flex flex-col transition-all duration-300`}>
            <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden">
              {/* Toolbar Controls */}
              <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-100">
                <div className="flex items-center flex-wrap gap-2.5">
                  {/* Filter Icon Button */}
                  <div className="relative group">
                    <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer">
                      <IconFilter size={17} stroke={2} />
                    </button>
                  </div>

                  {/* Search Sliding Input Box */}
                  <div className="relative flex items-center group">
                    <div
                      onClick={() => {
                        if (!showSearch) {
                          setShowSearch(true);
                        }
                      }}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                        showSearch
                          ? "w-48 px-2 cursor-default"
                          : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                      }`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSearch(!showSearch);
                        }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                      >
                        <IconSearch size={17} stroke={2.5} />
                      </button>
                      {showSearch && (
                        <>
                          <input
                            type="text"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                            className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                          />
                          {searchQuery && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery("");
                              }}
                              className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                            >
                              <IconX size={14} stroke={2.5} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Refresh Button */}
                  <div className="relative group">
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setEntriesLimit(10);
                        setShowSearch(false);
                      }}
                      className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                      title="Reset"
                    >
                      <IconRefresh size={17} stroke={2.5} />
                    </button>
                  </div>

                  {/* Import Button */}
                  <div className="relative group">
                    <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer" title="Import">
                      <IconUpload size={17} stroke={2.5} />
                    </button>
                  </div>

                  {/* Export Button */}
                  <div className="relative group">
                    <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer" title="Export">
                      <IconDownload size={17} stroke={2.5} />
                    </button>
                  </div>

                  {/* Entries Selector component */}
                  <EntriesSelector
                    value={entriesLimit}
                    options={[10, 20, 50]}
                    onChange={(val) => setEntriesLimit(val)}
                  />

                  {/* Add button inside toolbar */}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingDeptId(null);
                      setDeptName("");
                      setSidebarMode("ADD");
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                    title="Add Department"
                  >
                    <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
                  </button>
                </div>
              </div>

              <div className="pb-4">
                {isLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading departments...</div>
                ) : displayedDepartments.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No departments found.</div>
                ) : (
                  <ListView
                    columns={activeColumns}
                    data={displayedDepartments}
                    trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                    mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                    mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh]"
                    scrollbarClassName="custom-scrollbar"
                    thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
                  />
                )}
                
                <div className="py-4 flex justify-center">
                  <div className="flex items-center gap-1">
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                      disabled
                    >
                      <IconChevronLeft size={14} stroke={2.5} />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold bg-[#FBD037] border-[#FBD037] text-[#131327] transition shadow-sm">
                      1
                    </button>
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                      disabled
                    >
                      <IconChevronRight size={14} stroke={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {sidebarMode && (
            <div className="col-span-12 xl:col-span-5 animate-in fade-in slide-in-from-right-5 duration-300">
              {renderSidebar()}
            </div>
          )}

      
        </div>
      ) : (
        /* Hierarchy View Chart */
        <div className="w-full overflow-x-auto bg-[#F9FAFB] rounded-3xl border border-gray-100 shadow-inner select-none mb-10">
          <div className="org-tree min-w-max">
            <ul>
              <TreeNode node={departmentTree} isRoot={true} />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
